import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { BadgeQuestionMark } from "lucide-react";
import useAuth from "../hooks/useAuth";
import useTour from "../tour/useTour";
import { resolvePageHelp } from "./resolvePageHelp";
import useGuidedTourNarration from "./useGuidedTourNarration";
import { isGuidedTourNarrationEnabled } from "./guidedTourFlags";
import { dispatchOpenSettingsForTour } from "./pageHelpEvents";
import {
  GUIDED_TOUR_ACTION_BUTTON_SX,
  GUIDED_TOUR_POPOVER_PAPER_SX,
} from "./guidedTourPopoverStyles";

const SETTINGS_TOUR_DELAY_MS = 450;

export default function TopBarHelpMenu() {
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";
  const textColor = isLight ? "#1e293b" : "#ffffff";
  const isMobileWidth = useMediaQuery(theme.breakpoints.down("lg"));
  const isShortHeight = useMediaQuery("(max-height: 500px)");
  const isMobileLayout = isMobileWidth || isShortHeight;

  const location = useLocation();
  const { role, user } = useAuth();
  const { startTour, isOpen: isTourOpen, endTour } = useTour();
  const { speak, stop, isSpeaking } = useGuidedTourNarration();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const help = useMemo(
    () =>
      resolvePageHelp(location.pathname, {
        role,
        user,
        isMobileLayout,
      }),
    [location.pathname, role, user, isMobileLayout]
  );

  const narrationEnabled = isGuidedTourNarrationEnabled(isMobileLayout);
  const speechSupported =
    typeof window !== "undefined" && "speechSynthesis" in window;

  const closePopover = () => setAnchorEl(null);

  const runTour = (steps) => {
    if (!steps?.length || isTourOpen) return;
    closePopover();
    window.setTimeout(() => startTour(steps, null), 120);
  };

  const handlePlayOverview = () => {
    if (!help.narrationScript || isTourOpen) return;
    closePopover();
    if (isSpeaking) {
      stop();
    } else {
      speak(help.narrationScript);
    }
  };

  const handleSettingsTour = () => {
    if (!help.settingsTour?.steps?.length || isTourOpen) return;
    const steps = help.settingsTour.steps;
    closePopover();
    if (help.settingsTour.needsSettingsExpanded) {
      dispatchOpenSettingsForTour();
    }
    const delay = help.settingsTour.needsSettingsExpanded
      ? SETTINGS_TOUR_DELAY_MS
      : 120;
    window.setTimeout(() => {
      startTour(steps, null);
    }, delay);
  };

  return (
    <>
      <Tooltip title="Help">
        <IconButton
          aria-label="Help"
          data-tour="topbar-help"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            color: textColor,
            opacity: 0.8,
            "&:hover": {
              opacity: 1,
              bgcolor: isLight ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
            },
          }}
        >
          <BadgeQuestionMark size={20} strokeWidth={1.75} />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: { sx: GUIDED_TOUR_POPOVER_PAPER_SX },
        }}
      >
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              Help
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {help.pageLabel}
            </Typography>
          </Box>

          {isTourOpen && (
            <Typography variant="caption" color="text.secondary">
              A guided tour is already running. Stop it to start another.
            </Typography>
          )}

          {!isTourOpen && (
            <Stack spacing={1}>
              {help.guidedTour?.steps?.length > 0 && (
                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  onClick={() => runTour(help.guidedTour.steps)}
                  sx={GUIDED_TOUR_ACTION_BUTTON_SX}
                >
                  {help.guidedTour.label}
                </Button>
              )}

              {help.mainNavTour?.steps?.length > 0 && (
                <Button
                  variant={help.guidedTour ? "outlined" : "contained"}
                  size="small"
                  fullWidth
                  onClick={() => runTour(help.mainNavTour.steps)}
                  sx={GUIDED_TOUR_ACTION_BUTTON_SX}
                >
                  {help.mainNavTour.label}
                </Button>
              )}

              {help.settingsTour?.steps?.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={handleSettingsTour}
                  sx={GUIDED_TOUR_ACTION_BUTTON_SX}
                >
                  {help.settingsTour.label}
                </Button>
              )}

              {narrationEnabled &&
                help.narrationScript &&
                speechSupported && (
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    onClick={handlePlayOverview}
                    sx={GUIDED_TOUR_ACTION_BUTTON_SX}
                  >
                    {isSpeaking
                      ? "Stop page overview"
                      : `Play ${help.pageLabel} overview`}
                  </Button>
                )}

              {!help.hasAnyAction && (
                <Typography variant="body2" color="text.secondary">
                  No guided help is available for this page yet.
                </Typography>
              )}
            </Stack>
          )}

          {isTourOpen && (
            <Button
              size="small"
              variant="outlined"
              fullWidth
              onClick={() => {
                endTour();
                closePopover();
              }}
              sx={GUIDED_TOUR_ACTION_BUTTON_SX}
            >
              Stop tour
            </Button>
          )}
        </Stack>
      </Popover>
    </>
  );
}
